import type { NonConformityRow } from '@obraplan/core/application/use-cases/quality/non-conformity-use-cases';
import { ncNaFila, type OutboxEntry } from '@/app/outbox-types';
import { syncDoCartao, type SyncDoCartao } from '@/features/planning/fila-da-conferencia';

/**
 * A aba Não conformidades com a fila do aparelho por cima (Fase 43, RN-623).
 *
 * Encerrar com a solução, pôr e tirar foto sem sinal: a linha já mostra a NC resolvida e a contagem
 * de fotos de depois, com a marca "aguardando envio" — como o cartão da Conferência (ADR-195). É
 * previsão: quem decide é o servidor, quando a operação subir, e a recusa volta para a linha.
 */

/** O que a fila tem de uma NC: a solução, as fotos e as que saem da galeria dela. */
export function filaDaNc(entries: readonly OutboxEntry[], nonConformityId: string): OutboxEntry[] {
  const chave = ncNaFila(nonConformityId);
  return entries.filter(
    ({ operation }) =>
      operation.resourceId === chave ||
      ((operation.kind === 'fotos-da-nc' || operation.kind === 'resolver-nc') &&
        operation.nonConformityId === nonConformityId),
  );
}

/**
 * As linhas com o que a fila vai fazer nelas, na ordem em que foi feito. `quem` é o nome de quem
 * está com a sessão: é quem vai aparecer como "resolvida por" quando subir.
 */
export function ncsComAFila(
  rows: readonly NonConformityRow[],
  entries: readonly OutboxEntry[],
  quem: string,
): NonConformityRow[] {
  if (entries.length === 0) return [...rows];
  return rows.map((row) => {
    let atual = row;
    for (const { operation, createdAt } of filaDaNc(entries, row.id)) {
      if (operation.kind === 'resolver-nc' && atual.status === 'OPEN') {
        atual = {
          ...atual,
          status: 'RESOLVED',
          solution: operation.solution.trim(),
          resolvedAt: createdAt,
          resolvedByName: quem,
        };
      } else if (operation.kind === 'fotos-da-nc') {
        atual = { ...atual, photoCount: atual.photoCount + operation.files.length };
      } else if (operation.kind === 'remover-foto') {
        atual = { ...atual, photoCount: Math.max(atual.photoCount - 1, 0) };
      }
    }
    return atual;
  });
}

/** A marca da linha: "aguardando envio", ou o recusado com o motivo (RN-608). */
export function syncDaNc(
  entries: readonly OutboxEntry[],
  nonConformityId: string,
): SyncDoCartao | undefined {
  return syncDoCartao(filaDaNc(entries, nonConformityId));
}
